/**
 * API functions for event notification settings
 */
import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type { EventNotificationSetting, NotificationType } from '../../types/database';

export interface NotificationSettingWithAdmins extends EventNotificationSetting {
  admins?: Array<{ id: string; full_name: string; email: string }>;
}

/**
 * Get all notification settings for an event
 */
export async function getEventNotificationSettings(
  eventId: string
): Promise<NotificationSettingWithAdmins[]> {
  const { data, error } = await supabase
    .from('event_notification_settings')
    .select('*')
    .eq('event_id', eventId);

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Get a specific notification setting
 */
export async function getNotificationSetting(
  eventId: string,
  notificationType: NotificationType
): Promise<EventNotificationSetting | null> {
  const { data, error } = await supabase
    .from('event_notification_settings')
    .select('*')
    .eq('event_id', eventId)
    .eq('notification_type', notificationType)
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  return data ?? null;
}

/**
 * Create or update a notification setting for an event
 */
export async function upsertNotificationSetting(
  eventId: string,
  notificationType: NotificationType,
  recipientAdminIds: string[],
  enabled: boolean = true
): Promise<EventNotificationSetting> {
  const { data, error } = await supabase
    .from('event_notification_settings')
    .upsert(
      {
        event_id: eventId,
        notification_type: notificationType,
        recipient_admin_ids: recipientAdminIds,
        enabled,
      },
      {
        onConflict: 'event_id,notification_type',
      }
    )
    .select()
    .single();

  if (error) throw normalizeSupabaseError(error);
  return data;
}

/**
 * Update notification settings for an event (bulk update)
 */
export async function updateEventNotificationSettings(
  eventId: string,
  settings: Array<{
    notification_type: NotificationType;
    recipient_admin_ids: string[];
    enabled: boolean;
  }>
): Promise<EventNotificationSetting[]> {
  const results: EventNotificationSetting[] = [];

  for (const setting of settings) {
    const result = await upsertNotificationSetting(
      eventId,
      setting.notification_type,
      setting.recipient_admin_ids,
      setting.enabled
    );
    results.push(result);
  }

  return results;
}

/**
 * Toggle a notification type on/off for an event
 */
export async function toggleNotification(
  eventId: string,
  notificationType: NotificationType,
  enabled: boolean
): Promise<void> {
  const { error } = await supabase
    .from('event_notification_settings')
    .update({ enabled })
    .eq('event_id', eventId)
    .eq('notification_type', notificationType);

  if (error) throw normalizeSupabaseError(error);
}

/**
 * Delete a notification setting
 */
export async function deleteNotificationSetting(
  eventId: string,
  notificationType: NotificationType
): Promise<void> {
  const { error } = await supabase
    .from('event_notification_settings')
    .delete()
    .eq('event_id', eventId)
    .eq('notification_type', notificationType);

  if (error) throw normalizeSupabaseError(error);
}

/**
 * Get all admin users (for selecting notification recipients)
 */
export async function getAdminUsers(): Promise<
  Array<{ id: string; full_name: string; email: string }>
> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('id, full_name, email')
    .order('full_name');

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Initialize default notification settings for a new event
 */
export async function initializeEventNotifications(
  eventId: string,
  defaultAdminIds: string[]
): Promise<void> {
  const notificationTypes: NotificationType[] = [
    'new_application',
    'application_updated',
    'application_approved',
    'application_rejected',
    'deadline_reminder',
  ];

  for (const type of notificationTypes) {
    // For applicant notifications, don't set admin recipients
    const isApplicantNotification = type === 'application_approved' || type === 'application_rejected';

    await upsertNotificationSetting(
      eventId,
      type,
      isApplicantNotification ? [] : defaultAdminIds,
      true
    );
  }
}

/**
 * Send a test notification (calls Edge Function directly)
 * Test emails are sent to the provided testRecipientEmail (usually the current admin)
 */
export async function sendTestNotification(
  eventId: string,
  notificationType: NotificationType,
  testRecipientEmail: string
): Promise<{ success: boolean; message: string }> {
  const { data, error } = await supabase.functions.invoke('send-notification', {
    body: {
      notification_type: notificationType,
      event_id: eventId,
      test_mode: true,
      test_recipient_email: testRecipientEmail,
    },
  });

  if (error) {
    return { success: false, message: error.message };
  }

  return { success: true, message: data?.message || 'Test notification sent' };
}

/**
 * Resend the admin notification email for a specific application to configured recipients.
 */
export async function resendAdminApplicationNotification(
  eventId: string,
  applicationId: string,
  notificationType: 'new_application' | 'application_updated' = 'new_application',
): Promise<{ success: boolean; message: string }> {
  const { data, error } = await supabase.functions.invoke('send-notification', {
    body: {
      notification_type: notificationType,
      event_id: eventId,
      application_id: applicationId,
      resend: true,
    },
  });

  if (error) {
    return { success: false, message: error.message };
  }

  if (data?.error) {
    return { success: false, message: String(data.error) };
  }

  return { success: true, message: data?.message || 'Notification resent' };
}
